// authService.js - 權限管理核心服務

class AuthService {
    constructor() {
        this.currentUser = null;
        this.restoreSession();
    }

    /**
     * 從 localStorage 恢復登入狀態
     */
    restoreSession() {
        try {
            const authData = localStorage.getItem('authData');
            if (authData) {
                this.currentUser = JSON.parse(authData);
                console.log('✅ Session restored for user:', this.currentUser?.userName);
            }
        } catch (error) {
            console.error('❌ Failed to restore session:', error);
            this.logout();
        }
    }

    /**
     * 登入並儲存權限資料
     * @param {Object} loginResponse - 後端登入回應
     */
    login(loginResponse) {
        const {
            Token,
            Expiration,
            UserId,
            UserName,
            NickName,
            Roles,
            ProfileImage,
            RequirePasswordChange,
            Permissions = {},
            PermissionVersion = ''
        } = loginResponse;

        // 儲存完整的認證資料
        const authData = {
            token: Token,
            expiration: Expiration,
            userId: UserId,
            userName: UserName,
            nickName: NickName,
            roles: Array.isArray(Roles) ? Roles : [Roles],
            profileImage: ProfileImage || '/default-profile.png',
            requirePasswordChange: RequirePasswordChange === true,
            permissions: Permissions,
            permissionVersion: PermissionVersion
        };

        // 存入 localStorage
        localStorage.setItem('authData', JSON.stringify(authData));

        // 向後相容：保留舊的儲存方式
        localStorage.setItem('authToken', Token);
        localStorage.setItem('tokenExpiration', Expiration);
        localStorage.setItem('userId', UserId || '');
        localStorage.setItem('userName', UserName || '');
        localStorage.setItem('nickName', NickName || '');
        localStorage.setItem('roles', JSON.stringify(authData.roles));
        localStorage.setItem('profileImage', authData.profileImage);
        localStorage.setItem('requirePasswordChange', authData.requirePasswordChange ? 'true' : 'false');

        this.currentUser = authData;

        console.log('✅ Login successful, permissions loaded:', Object.keys(Permissions).length);
        return authData;
    }

    /**
     * 登出並清除所有資料
     */
    logout() {
        localStorage.clear();
        this.currentUser = null;
        console.log('🚪 User logged out');
    }

    /**
     * 檢查是否有某個權限（Own 或 All 都算有）
     * @param {string} permissionName - 權限名稱，例如 'Users.Read'
     * @returns {boolean}
     */
    hasPermission(permissionName) {
        if (!this.currentUser || !this.currentUser.permissions) {
            return false;
        }

        const perm = this.currentUser.permissions[permissionName];
        return perm?.HasPermission ?? false; // 修正：hasPermission → HasPermission
    }

    /**
     * 檢查是否有完整權限（必須是 All）
     * @param {string} permissionName - 權限名稱
     * @returns {boolean}
     */
    hasFullPermission(permissionName) {
        if (!this.currentUser || !this.currentUser.permissions) {
            return false;
        }

        const perm = this.currentUser.permissions[permissionName];
        return perm?.HasFullPermission ?? false; // 修正：hasFullPermission → HasFullPermission
    }

    /**
     * 取得權限範圍值 (0=None, 1=Own, 2=All)
     * @param {string} permissionName
     * @returns {number}
     */
    getPermissionScope(permissionName) {
        if (!this.currentUser || !this.currentUser.permissions) {
            return 0;
        }

        const perm = this.currentUser.permissions[permissionName];
        return perm?.ScopeValue ?? 0;
    }

    /**
     * 按分類取得所有權限
     * @param {string} category - 分類名稱，例如 'Users', 'Listings'
     * @returns {Object}
     */
    getPermissionsByCategory(category) {
        if (!this.currentUser || !this.currentUser.permissions) {
            return {};
        }

        return Object.fromEntries(
            Object.entries(this.currentUser.permissions)
                .filter(([_, info]) => info.Category === category)
        );
    }

    /**
     * 檢查使用者是否有某個角色
     * @param {string} roleName - 角色名稱，例如 'Admin'
     * @returns {boolean}
     */
    hasRole(roleName) {
        if (!this.currentUser || !this.currentUser.roles) {
            return false;
        }
        return this.currentUser.roles.includes(roleName);
    }

    /**
     * 取得目前使用者資訊
     * @returns {Object|null}
     */
    getCurrentUser() {
        return this.currentUser;
    }

    /**
     * 檢查 Token 是否過期
     * @returns {boolean}
     */
    isTokenExpired() {
        if (!this.currentUser || !this.currentUser.expiration) {
            return true;
        }
        return new Date(this.currentUser.expiration) < new Date();
    }

    /**
     * 檢查權限版本（可用於判斷是否需要重新登入）
     * @returns {string}
     */
    getPermissionVersion() {
        return this.currentUser?.permissionVersion || '';
    }

    /**
     * Debug 工具：列出所有權限
     */
    debugPermissions() {
        if (process.env.NODE_ENV === 'production') return;
        if (!this.currentUser || !this.currentUser.permissions) {
            console.log('❌ No permissions loaded');
            return;
        }

        console.group('🔑 Current User Permissions');
        console.log('User:', this.currentUser.nickName);
        console.log('Roles:', this.currentUser.roles);
        console.table(
            Object.entries(this.currentUser.permissions).map(([name, info]) => ({
                Permission: name,
                Scope: info.Scope,
                HasPermission: info.HasPermission,
                HasFullPermission: info.HasFullPermission,
                Category: info.Category
            }))
        );
        console.groupEnd();
    }
}

// 單例模式：整個應用只有一個實例
const authService = new AuthService();

export default authService;